(function () {
  'use strict';

  window.theme = window.theme || {};
  if (window.theme.componentLoaderBound) return;
  window.theme.componentLoaderBound = true;

  // ============================================================
  // Surface registry
  // ------------------------------------------------------------
  // Replaces three legacy loaders: cart-loader.js, search-loader.js,
  // mobile-menu-loader.js. Adds a fourth surface (overlay) for the
  // overlay primitive (component-overlay.css/js).
  //
  // Per surface contract:
  //   name              — identifier; backs the per-surface loaded flag
  //                       (window.theme.<name>Loaded).
  //   triggerSelector   — CSS selector for delegated click triggers.
  //   pointerGuard      — when true, bail on modifier keys + non-left-click.
  //   bailIf            — optional pre-load predicate.
  //   urlTrigger        — optional location.search substring.
  //   extraTriggerEvent — optional second event binding (e.g. focusin). Single
  //                       {type, selector}; matched with .matches (target IS the
  //                       element). Used by 'search'.
  //   preloadEvents     — optional ARRAY of {type, selector} warm-up bindings,
  //                       matched with .closest (target is a DESCENDANT of the
  //                       zone) and loaded-guarded. Use bubbling events only
  //                       (pointerover, NOT pointerenter). Used by collectionFacets.
  //   assetKeys         — primary [css, js] keys on window.theme.assets. A null
  //                       css key (assetKeys[0]) marks a JS-only surface: the
  //                       primary CSS presence-check + injection are skipped.
  //   secondaryAssetKeys— optional [css, js] pair, chained after primary.
  //   openEvent         — event dispatched after assets load.
  //   externalOpenEvent — event listened for; triggers the same load path.
  //   passTrigger       — when true, openEvent dispatches with detail.trigger.
  // ============================================================
  var surfaces = [
    {
      name: 'cart',
      triggerSelector: '[data-cart-trigger]',
      pointerGuard: true,
      bailIf: function () {
        return (window.theme && window.theme.settings && window.theme.settings.cart_type) === 'page';
      },
      urlTrigger: 'cart_open=1',
      assetKeys: ['cartCss', 'cartJs'],
      secondaryAssetKeys: ['cartBlocksCss', 'cartBlocksJs'],
      tertiaryCssKey: 'cartSharedCss',
      openEvent: 'cart:open',
      externalOpenEvent: 'cart:open'
    },
    {
      name: 'search',
      triggerSelector: '[data-search-trigger]',
      extraTriggerEvent: { type: 'focusin', selector: 'input[type="search"]' },
      assetKeys: ['searchCss', 'searchJs'],
      openEvent: 'search:open',
      externalOpenEvent: 'search:open'
    },
    {
      name: 'mobileMenu',
      triggerSelector: '[data-mobile-menu-trigger]',
      pointerGuard: true,
      assetKeys: ['mobileMenuCss', 'mobileMenuJs'],
      openEvent: 'mobile-menu:open',
      externalOpenEvent: 'mobile-menu:open'
    },
    {
      name: 'overlay',
      triggerSelector: '[data-overlay-trigger]',
      assetKeys: ['overlayCss', 'overlayJs'],
      openEvent: 'overlay:open',
      externalOpenEvent: 'overlay:open',
      passTrigger: true
    },
    {
      // Collection facets engine. JS-only surface — the facets CSS is loaded
      // eagerly by sections/main-collection.liquid (it styles facets visible on
      // initial render); the loader handles ONLY the JS, deferring its parse/exec
      // until first filter engagement. assetKeys[0] is null → JS-only path.
      name: 'collectionFacets',
      triggerSelector: '[data-filters-toggle]',        // the Filters BUTTON only — NOT facet links (preventDefault would kill a cold link click)
      preloadEvents: [                                  // warm the engine on hover/focus over the facet area + sort select
        { type: 'pointerover', selector: '[data-collection-facets-zone], [data-collection-sort]' },
        { type: 'focusin',     selector: '[data-collection-facets-zone], [data-collection-sort]' }
      ],
      urlTrigger: 'filter.',                            // a filtered landing page loads the engine eagerly
      assetKeys: [null, 'collectionFacetsJs'],          // JS-only — CSS is eager via the section
      openEvent: 'collection-facets:open',
      passTrigger: true                                 // openEvent carries detail.trigger for the cold-first-open bridge
    },
    {
      // Help widget (Slice 28) — non-modal corner-docked decision-tree chat.
      // Tier-1 surface: launcher + inert <template> ship eagerly; the panel CSS +
      // engine JS inject only on the first [data-help-trigger] click. openEvent
      // carries detail.trigger so the engine knows which element opened it.
      name: 'helpWidget',
      triggerSelector: '[data-help-trigger]',
      assetKeys: ['helpWidgetCss', 'helpWidgetJs'],
      openEvent: 'help:open',
      externalOpenEvent: 'help:open',   // P2: programmatic reopen (contact handoff) drives the load path
      passTrigger: true
    },
    {
      // Quick-add (Slice 29 P2) — lazy controller + product-info bundle, injected
      // on the first [data-quick-add-trigger] click. The overlay engine is ensured
      // by the controller itself (see component-quick-add.js). openEvent carries
      // detail.trigger so the controller reads the trigger's data attributes.
      name: 'quickAdd',
      triggerSelector: '[data-quick-add-trigger]',
      assetKeys: ['quickAddCss', 'quickAddJs'],
      secondaryAssetKeys: ['productInfoCss', 'productInfoJs'],
      openEvent: 'quick-add:open',
      passTrigger: true
    },
    {
      // Wishlist drawer (Slice 29 P4) — lazy drawer shell + the shared cards
      // engine, injected on the first [data-wishlist-drawer-trigger] click (the
      // header icon in drawer mode) OR on the cold-start wishlist:added auto-open
      // path (handled below, gated by the drawer shell's data-attrs). Secondary =
      // the product-card BASE css (pre-warms card styling; the per-layout card
      // CSS self-loads via each injected bridge fragment's C15 <link>) paired
      // with the shared cards engine JS. openEvent carries detail.trigger.
      name: 'wishlist',
      triggerSelector: '[data-wishlist-drawer-trigger]',
      assetKeys: ['wishlistDrawerCss', 'wishlistDrawerJs'],
      secondaryAssetKeys: ['productCardBaseCss', 'wishlistCardsJs'],
      openEvent: 'wishlist:open',
      passTrigger: true
    },
    {
      // Popup engine (PERF-PASS P4) — lazy surface for ALL popups (everything routed
      // through popup-shell.liquid + the contact-popup dialog). No triggerSelector:
      // clicks are handled by the dedicated handlePopupTrigger (id must be resolved
      // from the DOM), and the engine self-arms auto-triggers once loaded. Presence+idle
      // load after window load when a [data-popup-dialog] exists; immediate load on the
      // first [data-popup-open]/FAB click before idle fires.
      name: 'popup',
      assetKeys: ['popupEngineCss', 'popupEngineJs'],
      openEvent: 'popup:open'
    },
    {
      // Sticky add-to-cart (Slice 33) — scroll-armed asset injection. No click
      // path here: popup clicks route through the existing quickAdd surface;
      // direct-add + back-to-top are owned by the controller. This surface only
      // injects CSS+JS the first time the buy form's [data-atc-trigger] scrolls
      // above the viewport, then hands off via 'sticky-atc:armed' (one-shot IO;
      // ongoing show/hide is the controller's own IO). The observed node is
      // replaced on every SRA variant swap — re-observed on ioReacquireEvent
      // until the surface loads.
      name: 'stickyAtc',
      ioPresenceSelector: '[data-sticky-atc]',
      ioTriggerSelector: '[data-atc-trigger]',
      ioCondition: 'above',
      ioReacquireEvent: ['product:variant-changed', 'shopify:section:load'],
      assetKeys: ['stickyAtcCss', 'stickyAtcJs'],
      openEvent: 'sticky-atc:armed'
    }
  ];

  function findSurface(name) {
    for (var s = 0; s < surfaces.length; s++) {
      if (surfaces[s].name === name) return surfaces[s];
    }
    return null;
  }

  // Cold-start auto-open: adding a product to the wishlist opens the drawer,
  // gated by reading the always-present drawer shell's data-attrs — mirrors how
  // main.js reads cart-drawer[data-atc-auto-open]. Once the drawer is loaded,
  // its own wishlist:added listener owns subsequent (warm) adds, so this bails
  // when the surface is already loaded. No js-variables / window.theme.settings
  // lookup — the gate lives on the DOM.
  function handleWishlistAdded(event) {
    if (window.theme.wishlistLoaded) return;
    var dr = document.querySelector('wishlist-drawer[data-auto-open-on-add]');
    if (!dr || dr.dataset.autoOpenOnAdd !== 'true' || dr.dataset.wishlistDestination === 'page') return;
    var handle = event.detail && event.detail.handle;
    if (handle) dr.dataset.pendingHighlight = handle;
    var surface = findSurface('wishlist');
    if (surface) loadSurface(surface, null);
  }

  function loadedFlagKey(surface) {
    return surface.name + 'Loaded';
  }

  function dispatchOpenEvent(surface, trigger) {
    if (surface.passTrigger && trigger) {
      document.dispatchEvent(new CustomEvent(surface.openEvent, {
        detail: { trigger: trigger, source: 'click' }
      }));
    } else {
      document.dispatchEvent(new CustomEvent(surface.openEvent));
    }
  }

  function injectStylesheet(href) {
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  function injectScript(src, onLoad) {
    var script = document.createElement('script');
    script.type = 'module';
    script.src = src;
    if (typeof onLoad === 'function') {
      script.onload = onLoad;
    }
    document.head.appendChild(script);
  }

  // Asset-presence validation, shared by loadSurface + warm. Returns false (and
  // warns) when a required asset URL is absent; true when the surface is safe to
  // inject. A null/falsy primaryCss (assetKeys[0]) marks a JS-only surface — the
  // CSS is loaded elsewhere (e.g. eagerly by the section) so only the JS is checked.
  function assetsPresent(surface) {
    var assets = window.theme.assets || {};
    var primaryCss = surface.assetKeys[0];
    var primaryJs = surface.assetKeys[1];
    if (!assets[primaryJs] || (primaryCss && !assets[primaryCss])) {
      console.warn('[component-loader] Missing asset URLs for ' + primaryCss + ' / ' + primaryJs);
      return false;
    }
    if (surface.secondaryAssetKeys) {
      var secCss = surface.secondaryAssetKeys[0];
      var secJs = surface.secondaryAssetKeys[1];
      if (!assets[secCss] || !assets[secJs]) {
        console.warn('[component-loader] Missing secondary asset URLs for ' + secCss + ' / ' + secJs);
        return false;
      }
    }
    return true;
  }

  // Inject a surface's assets exactly once: set the loaded flag, inject ALL CSS
  // in parallel up-front (primary + secondary + tertiary — both stylesheets begin
  // downloading immediately rather than sequencing the secondary CSS behind the
  // primary JS load; skipped for JS-only surfaces), then chain JS primary→secondary
  // and fire onComplete after the last JS onload. Callers validate with
  // assetsPresent() first. onComplete is the ONLY behavioural difference between
  // an open (dispatchOpenEvent) and a warm (onReady callback).
  function injectSurface(surface, onComplete) {
    var assets = window.theme.assets || {};
    var primaryCss = surface.assetKeys[0];
    var primaryJs = surface.assetKeys[1];

    window.theme[loadedFlagKey(surface)] = true;

    if (primaryCss) {
      injectStylesheet(assets[primaryCss]);
    }
    if (surface.secondaryAssetKeys) {
      injectStylesheet(assets[surface.secondaryAssetKeys[0]]);
    }
    if (surface.tertiaryCssKey && assets[surface.tertiaryCssKey]) {
      injectStylesheet(assets[surface.tertiaryCssKey]);
    }

    injectScript(assets[primaryJs], function () {
      if (surface.secondaryAssetKeys) {
        injectScript(assets[surface.secondaryAssetKeys[1]], function () {
          if (typeof onComplete === 'function') onComplete();
        });
      } else {
        if (typeof onComplete === 'function') onComplete();
      }
    });
  }

  function loadSurface(surface, trigger) {
    if (window.theme[loadedFlagKey(surface)]) {
      dispatchOpenEvent(surface, trigger);
      return;
    }
    if (!assetsPresent(surface)) return;
    injectSurface(surface, function () {
      dispatchOpenEvent(surface, trigger);
    });
  }

  // Warm a surface's assets WITHOUT opening it. Reuses the same loaded flag as
  // loadSurface, so a later real trigger for the same surface short-circuits to
  // dispatchOpenEvent only (no second injection, no duplicate handlers). onReady
  // fires after the surface's JS finishes loading — for the wishlist surface that
  // is after wishlistCardsJs, so window.theme.wishlistCards is guaranteed present.
  // If the surface is already loaded, onReady fires immediately (no re-inject, no
  // open event). Consumed by the cart-drawer mini-cart wishlist tab (Slice 29 P5).
  function warm(surfaceName, onReady) {
    var surface = findSurface(surfaceName);
    if (!surface) return;
    if (window.theme[loadedFlagKey(surface)]) {
      if (typeof onReady === 'function') onReady();
      return;
    }
    if (!assetsPresent(surface)) return;
    injectSurface(surface, function () {
      if (typeof onReady === 'function') onReady();
    });
  }

  window.theme.loader = window.theme.loader || {};   // extend, do not clobber
  window.theme.loader.warm = warm;

  function handleClick(event) {
    if (!event.target || !event.target.closest) return;
    // Each surface evaluates independently (matches the legacy three-loader
    // behavior where each loader bound its own click handler). A trigger
    // element matching multiple surface selectors fires all matching
    // surfaces; preventDefault is idempotent.
    for (var i = 0; i < surfaces.length; i++) {
      var surface = surfaces[i];
      if (!surface.triggerSelector) continue; // popup surface has no generic selector — handlePopupTrigger owns it
      var trigger = event.target.closest(surface.triggerSelector);
      if (!trigger) continue;
      if (surface.pointerGuard) {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) continue;
      }
      if (surface.bailIf && surface.bailIf()) continue;
      event.preventDefault();
      loadSurface(surface, trigger);
    }
  }

  // ---------- Popup surface (PERF-PASS P4) ----------
  // Dedicated click path: the popup section id must be resolved from the DOM (a
  // trigger carries a trigger-id / the FAB carries the section id), so this cannot
  // reuse the generic click machinery (which dispatches openEvent without detail.id).
  // Returns null for a [data-popup-open] that has no matching popup dialog (e.g. a
  // help-widget trigger reusing the same attribute) so we never falsely intercept it.
  function resolvePopupId(trigger) {
    if (trigger.hasAttribute('data-popup-launcher')) return trigger.getAttribute('data-popup-id');
    var tid = trigger.getAttribute('data-popup-open');
    if (!tid) return null;
    var esc = (window.CSS && CSS.escape) ? CSS.escape(tid) : tid;
    var dlg = document.querySelector('[data-popup-dialog][data-popup-trigger-id="' + esc + '"]');
    return dlg ? dlg.getAttribute('data-popup-id') : null;
  }

  // Immediate-click path: the race where the user clicks before the idle load fires.
  // Bails once the engine is loaded (it owns clicks from then on). injectSurface sets
  // popupLoaded synchronously; the engine registers its popup:open listener + runs
  // discover() (populating configs) before onComplete dispatches popup:open{id}.
  function handlePopupTrigger(event) {
    if (window.theme.popupLoaded) return;
    if (!event.target || !event.target.closest) return;
    var trigger = event.target.closest('[data-popup-open], [data-popup-launcher]');
    if (!trigger) return;
    var id = resolvePopupId(trigger);
    if (!id) return;
    if (trigger.tagName === 'A') event.preventDefault();
    var surface = findSurface('popup');
    if (!surface || !assetsPresent(surface)) return;
    injectSurface(surface, function () {
      document.dispatchEvent(new CustomEvent('popup:open', { detail: { id: id } }));
    });
  }

  // Presence + idle load: after window load, inject the engine (CSS + JS) once when a
  // [data-popup-dialog] exists. Covers ALL auto-triggers (timed/scroll/exit/cart) — the
  // engine self-arms + binds its own click/FAB listeners on discover(). Zero popups on
  // the page → no [data-popup-dialog] → zero popup requests.
  function idleLoadPopup() {
    if (window.theme.popupLoaded) return;
    if (!document.querySelector('[data-popup-dialog]')) return;
    var surface = findSurface('popup');
    if (!surface || !assetsPresent(surface)) return;
    injectSurface(surface, function () {});
  }

  function scheduleIdlePopup() {
    var ric = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };
    ric(idleLoadPopup);
  }

  function makeExtraTriggerHandler(surface) {
    return function (event) {
      if (!event.target || !event.target.matches) return;
      if (event.target.matches(surface.extraTriggerEvent.selector)) {
        loadSurface(surface, event.target);
      }
    };
  }

  // Separate from makeExtraTriggerHandler: preload events fire on DESCENDANTS of
  // a zone (facet input/link/select), never the zone element itself, so this
  // matches with .closest (not .matches). It also early-returns once the surface
  // is loaded — pointerover fires continuously as the pointer moves over child
  // nodes, so without the guard it would spam loadSurface/dispatchOpenEvent.
  function makePreloadHandler(surface, evt) {
    return function (event) {
      if (window.theme[loadedFlagKey(surface)]) return;
      if (!event.target || !event.target.closest) return;
      if (event.target.closest(evt.selector)) {
        loadSurface(surface, event.target);
      }
    };
  }

  function makeExternalOpenHandler(surface) {
    return function () {
      if (window.theme[loadedFlagKey(surface)]) return;
      if (surface.bailIf && surface.bailIf()) return;
      loadSurface(surface, null);
    };
  }

  // Generic scroll-armed surface loader (Slice 33). For each surface declaring
  // an ioTriggerSelector, when its ioPresenceSelector matches, observe the first
  // trigger match with ONE viewport-root IO. Condition 'above' fires when the
  // trigger leaves upward (!isIntersecting && boundingClientRect.top <
  // rootBounds.top). On first condition-met → loadSurface (CSS+JS injected
  // together, then openEvent) and disconnect — this IO exists ONLY to load;
  // ongoing visibility is the loaded surface's own concern. The observed node is
  // replaced by SRA swaps, so while still unloaded an ioReacquireEvent listener
  // re-queries + re-observes it. No behaviour change for existing surfaces (all
  // lack ioTriggerSelector).
  function armIoTriggers() {
    if (!('IntersectionObserver' in window)) return;   // no IO → the feature simply never arms
    for (var i = 0; i < surfaces.length; i++) {
      var s = surfaces[i];
      if (!s.ioTriggerSelector) continue;
      if (s.ioPresenceSelector && !document.querySelector(s.ioPresenceSelector)) continue;
      (function (surface) {
        var target = document.querySelector(surface.ioTriggerSelector);
        if (!target) return;                             // no trigger node → never arm, silent
        var reacquire = null;
        // ioReacquireEvent may be a string OR an array of event names.
        var reacquireEvents = surface.ioReacquireEvent ? [].concat(surface.ioReacquireEvent) : [];
        var io = new IntersectionObserver(function (entries) {
          for (var e = 0; e < entries.length; e++) {
            var en = entries[e];
            // rootBounds is null in a cross-origin iframe (the theme editor
            // preview) — treat the viewport top as 0 there.
            var rootTop = en.rootBounds ? en.rootBounds.top : 0;
            var above = !en.isIntersecting && en.boundingClientRect.top < rootTop;
            var met = surface.ioCondition === 'above' ? above : en.isIntersecting;
            if (met) {
              io.disconnect();
              if (reacquire) {
                for (var u = 0; u < reacquireEvents.length; u++) document.removeEventListener(reacquireEvents[u], reacquire);
              }
              loadSurface(surface, target);              // injects assets, then dispatches openEvent
              return;
            }
          }
        });
        if (reacquireEvents.length) {
          reacquire = function () {                       // SRA swap / editor re-render replaced the node → re-observe
            if (window.theme[loadedFlagKey(surface)]) return;
            io.disconnect();
            var t2 = document.querySelector(surface.ioTriggerSelector);
            if (t2) { target = t2; io.observe(t2); }
          };
          for (var b = 0; b < reacquireEvents.length; b++) document.addEventListener(reacquireEvents[b], reacquire);
        }
        io.observe(target);
      })(s);
    }
  }

  function handleURLTriggers() {
    var search = window.location.search || '';
    for (var i = 0; i < surfaces.length; i++) {
      var surface = surfaces[i];
      if (!surface.urlTrigger) continue;
      if (search.indexOf(surface.urlTrigger) === -1) continue;
      if (surface.bailIf && surface.bailIf()) continue;
      loadSurface(surface, null);
    }
  }

  function bind() {
    document.addEventListener('click', handleClick);
    document.addEventListener('click', handlePopupTrigger);
    document.addEventListener('wishlist:added', handleWishlistAdded);

    for (var i = 0; i < surfaces.length; i++) {
      var surface = surfaces[i];
      if (surface.extraTriggerEvent) {
        document.addEventListener(surface.extraTriggerEvent.type, makeExtraTriggerHandler(surface));
      }
      if (surface.preloadEvents) {
        for (var p = 0; p < surface.preloadEvents.length; p++) {
          var evt = surface.preloadEvents[p];
          document.addEventListener(evt.type, makePreloadHandler(surface, evt));
        }
      }
      if (surface.externalOpenEvent) {
        document.addEventListener(surface.externalOpenEvent, makeExternalOpenHandler(surface));
      }
    }

    handleURLTriggers();
    armIoTriggers();

    // Popup engine: presence + idle load after window load (PERF-PASS P4, D5).
    if (document.readyState === 'complete') scheduleIdlePopup();
    else window.addEventListener('load', scheduleIdlePopup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
